import * as React from "react"
import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"

const Accordion = AccordionPrimitive.Root

const AccordionItem = React.forwardRef(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item ref={ref} className={cn("border-b", className)} {...props} />
))
AccordionItem.displayName = "AccordionItem"

const AccordionTrigger = React.forwardRef(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex flex-1 items-center justify-between py-4 text-sm font-medium transition-all hover:underline text-left [&[data-state=open]>svg]:rotate-180",
        className
      )}
      {...props}>
      {children}
      <ChevronDown
        className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
))
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName

// forceMount + the print:!block/h-auto override below exist for one reason:
// this project's only consumer is the client report (see VisibilityTab.js /
// FindingsList.js), whose "Als PDF speichern" button just calls
// window.print() on the live page. Without forceMount, Radix doesn't put
// closed content in the DOM at all — there is nothing for a print
// stylesheet to reveal.
//
// `data-[state=closed]:h-0` is load-bearing, not decorative: this Radix
// version's Content (unlike react-collapsible's standalone package) does
// NOT set the native `hidden` attribute even with forceMount — closed
// content only ever gets `animate-accordion-up` (height -> 0 over 0.2s).
// Without animation-fill-mode:forwards (tailwindcss-animate only applies
// that via an explicit fill-mode-* class, which this doesn't use), the
// element reverts to its un-animated height — i.e. fully expanded — the
// instant that 0.2s mount animation finishes. Found live: every
// forceMounted accordion in the report rendered permanently open on
// load, not just for print. The static h-0 gives "closed" a resting
// value for the animation to land back on; print:!h-auto still wins
// there via !important.
const AccordionContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    forceMount
    className="overflow-hidden text-sm data-[state=closed]:h-0 data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down print:!block print:!h-auto print:!overflow-visible print:!animate-none"
    {...props}>
    <div className={cn("pb-4 pt-0", className)}>{children}</div>
  </AccordionPrimitive.Content>
))
AccordionContent.displayName = AccordionPrimitive.Content.displayName

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
